import { Link as RouterLink } from "@tanstack/react-router";
import { Check, Copy, ExternalLink, ImageOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { conversionMessage, linkTypeLabel, needsCredentialAction, STORE_CHIPS } from "@/lib/affiliate-ui";
import { useClipboard } from "@/lib/hooks/useClipboard";
import type { ConversionResult } from "@/lib/hooks/useConvertLink";
import { formatPriceCents, marketplaceLabel } from "@/lib/marketplace";
import { safeHref } from "@/lib/short-links";
import { Notice } from "./Notice";

interface ConversionResultCardProps {
  readonly result: ConversionResult;
}

/** Resultado de UMA conversao: link oficial + dados do produto, ou erro claro. */
export function ConversionResultCard({ result }: ConversionResultCardProps): JSX.Element {
  const finalUrl = safeHref(result.finalUrl);
  if (result.status !== "converted" || !finalUrl) {
    return (
      <Notice tone="danger">
        <p className="font-medium">{conversionMessage(result.errorCode)}</p>
        {result.store && <p className="text-xs opacity-80">Loja: {marketplaceLabel(result.store)}</p>}
        {needsCredentialAction(result.errorCode) && (
          <RouterLink to="/app/account" className="text-xs font-semibold underline underline-offset-2">
            Abrir credenciais
          </RouterLink>
        )}
      </Notice>
    );
  }
  return <ConvertedLink result={result} finalUrl={finalUrl} />;
}

function ProductSummary({ result }: { result: ConversionResult }): JSX.Element {
  const product = result.product ?? null;
  const price = formatPriceCents(product?.priceCents ?? null);
  return (
    <div className="flex gap-3">
      <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-background/60">
        {product?.imageUrl ? (
          <img src={product.imageUrl} alt="" className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <ImageOff size={18} className="text-muted-foreground" aria-hidden="true" />
        )}
      </div>
      <div className="min-w-0 space-y-1">
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {result.store ? marketplaceLabel(result.store) : "Loja"} · {linkTypeLabel(result.linkType)}
        </p>
        <p className="line-clamp-2 text-sm font-medium text-foreground">{product?.title ?? "Link de afiliado gerado"}</p>
        {price && <p className="text-sm font-semibold text-[hsl(var(--gold))]">{price}</p>}
      </div>
    </div>
  );
}

function ConvertedLink({ result, finalUrl }: { result: ConversionResult; finalUrl: string }): JSX.Element {
  const { copiedKey, failed, copy } = useClipboard();
  const directOnly = STORE_CHIPS.find((chip) => chip.code === result.store)?.directOnly ?? false;
  return (
    <div className="space-y-3 rounded-lg border border-[hsl(var(--border-strong))] bg-[hsl(var(--surface-overlay)/0.75)] p-4">
      <ProductSummary result={result} />
      <div className="rounded-md border border-border bg-background/50 p-3">
        <p className="break-all font-mono text-sm text-foreground">{finalUrl}</p>
      </div>
      {directOnly && (
        <p className="text-xs text-muted-foreground">Esta loja não permite encurtador: o link sai direto no site oficial.</p>
      )}
      {failed && <p className="text-xs text-destructive">Não foi possível copiar. Selecione o link e copie manualmente.</p>}
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" onClick={() => void copy(finalUrl)}>
          {copiedKey ? <Check size={14} /> : <Copy size={14} />}
          {copiedKey ? "Copiado" : "Copiar link"}
        </Button>
        <Button asChild size="sm" variant="secondary">
          <a href={finalUrl} target="_blank" rel="noreferrer noopener">
            Abrir
            <ExternalLink size={14} />
          </a>
        </Button>
      </div>
    </div>
  );
}
